import { Controller } from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { FieldShell, type FieldComponentProps } from "./field-shell";

export function SelectField({
  field,
  control,
  disabled,
}: FieldComponentProps) {
  if (field.kind !== "select") return null;

  if (field.multiple) {
    return (
      <Controller
        name={field.name}
        control={control}
        render={({ field: f, fieldState }) => {
          const current: string[] = Array.isArray(f.value)
            ? (f.value as string[])
            : [];
          function toggle(value: string, checked: boolean) {
            if (checked) {
              if (!current.includes(value)) f.onChange([...current, value]);
            } else {
              f.onChange(current.filter((v) => v !== value));
            }
          }
          return (
            <FieldShell
              id={f.name}
              label={field.label}
              required={field.required}
              helpText={field.helpText}
              error={fieldState.error?.message}
            >
              <div className="space-y-1.5 rounded-md border p-2">
                {field.options.map((opt) => {
                  const checkedNow = current.includes(opt.value);
                  const id = `${f.name}-${opt.value}`;
                  return (
                    <label
                      key={opt.value}
                      htmlFor={id}
                      className="flex cursor-pointer items-center gap-2.5 rounded-sm px-1 py-1 text-sm hover:bg-muted"
                    >
                      <Checkbox
                        id={id}
                        checked={checkedNow}
                        onCheckedChange={(c) =>
                          toggle(opt.value, c === true)
                        }
                        disabled={disabled}
                      />
                      <span>{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            </FieldShell>
          );
        }}
      />
    );
  }

  return (
    <Controller
      name={field.name}
      control={control}
      render={({ field: f, fieldState }) => (
        <FieldShell
          id={f.name}
          label={field.label}
          required={field.required}
          helpText={field.helpText}
          error={fieldState.error?.message}
        >
          <Select
            value={f.value == null ? "" : String(f.value)}
            onValueChange={(v) => f.onChange(v)}
            disabled={disabled}
          >
            <SelectTrigger id={f.name} aria-invalid={!!fieldState.error}>
              <SelectValue
                placeholder={field.placeholder ?? "Select an option"}
              />
            </SelectTrigger>
            <SelectContent>
              {field.options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldShell>
      )}
    />
  );
}
